// Documents of a matter. The server keeps them in their own collection and
// serves them from GET /api/cases/:id/documents - the case payload carries
// only ObjectId references - so the matter adapter fetches them separately.

import { downloadStaffFile, getAuthHeaders, refusal } from "./auth";
import type { MatterDocument } from "../data/types";

export interface ServerDocument {
  _id: string;
  fileName?: string;
  fileType?: string;
  documentType?: string;
  source?: string;
  status?: string;
  statusReason?: string;
  createdAt?: string;
  publishedToClient?: boolean;
  subfolder?: string;
  workProduct?: boolean;
  aiResult?: {
    confidence?: number;
    /** How many of the fields this document type is asked for came
        back, and how many there are. Absent on documents extracted
        before the count existed. */
    fieldsFound?: number;
    fieldsExpected?: number;
  } | null;
}

// The server caps a page at 100 (routes/documents.ts), so a heavy matter
// needs several requests rather than a silent truncation.
const PAGE_SIZE = 100;

// Must start with a letter: Drive keeps names like "I-589 Smith 8.2026",
// whose trailing ".2026" is part of the title, not an extension.
const EXTENSION = /\.[A-Za-z][A-Za-z0-9]{0,4}$/;

function extensionOf(fileName: string, fileType: string | undefined): string {
  const match = fileName.match(EXTENSION);
  return match ? match[0].slice(1) : (fileType ?? "");
}

export function adaptDocument(d: ServerDocument): MatterDocument {
  const fileName = d.fileName ?? "(untitled)";
  return {
    id: d._id,
    title: fileName.replace(EXTENSION, ""),
    type: d.documentType,
    ext: extensionOf(fileName, d.fileType),
    uploadedBy: d.source === "client_upload" ? "Client" : "Firm",
    date: (d.createdAt ?? "").slice(0, 10),
    aiConfidence: d.aiResult?.confidence ?? undefined,
    aiFields:
      typeof d.aiResult?.fieldsFound === "number" && typeof d.aiResult?.fieldsExpected === "number"
        ? { found: d.aiResult.fieldsFound, expected: d.aiResult.fieldsExpected }
        : undefined,
    publishedToClient: Boolean(d.publishedToClient),
    subfolder: d.subfolder,
    workProduct: Boolean(d.workProduct),
    source: d.source,
    status: d.status,
    statusReason: d.statusReason,
  };
}

/** Never throws: a matter must still render when its documents fail to load. */
export async function fetchCaseDocuments(caseId: string): Promise<MatterDocument[]> {
  const out: MatterDocument[] = [];
  try {
    for (let page = 1; ; page++) {
      const r = await fetch(
        `/api/cases/${encodeURIComponent(caseId)}/documents?limit=${PAGE_SIZE}&page=${page}`,
        { headers: { ...getAuthHeaders() }, credentials: "same-origin" },
      );
      if (!r.ok) break;
      const body = (await r.json()) as { documents?: ServerDocument[]; totalPages?: number };
      out.push(...(body.documents ?? []).map(adaptDocument));
      if (!body.totalPages || page >= body.totalPages) break;
    }
  } catch {
    /* keep whatever arrived before the failure */
  }
  return out;
}

// The server's allow-list (config/upload.ts) - shared with the portal
// module, which has its own tighter size ceiling.
export const UPLOAD_ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp,.docx";
// The FIRM ceiling (env.MAX_FILE_SIZE default), not the portal's 25 MB.
export const FIRM_UPLOAD_MAX_BYTES = 50 * 1024 * 1024;

const ACCEPT_EXTS = new Set(UPLOAD_ACCEPT.split(",").map((s) => s.trim().replace(".", "")));
const MAX_MB = Math.round(FIRM_UPLOAD_MAX_BYTES / 1024 / 1024);

// Pre-checks are UX, not the contract: multer's size limit surfaces as a
// bare 500 (so size MUST be caught here), while its type filter answers a
// 400 whose words the upload helpers surface verbatim.
export function uploadProblem(file: File): string | null {
  const ext = file.name.match(/\.([A-Za-z0-9]+)$/)?.[1]?.toLowerCase() ?? "";
  if (!ACCEPT_EXTS.has(ext)) return `"${file.name}" is not an accepted type (${UPLOAD_ACCEPT}).`;
  if (file.size > FIRM_UPLOAD_MAX_BYTES) {
    return `"${file.name}" is over the ${MAX_MB} MB limit.`;
  }
  return null;
}

export async function uploadCaseDocument(caseId: string, file: File): Promise<MatterDocument> {
  const form = new FormData();
  form.append("file", file);
  const r = await fetch(`/api/cases/${encodeURIComponent(caseId)}/documents`, {
    method: "POST",
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
    body: form,
  });
  if (!r.ok) throw await refusal(r, `Upload failed (HTTP ${r.status})`);
  return adaptDocument((await r.json()) as ServerDocument);
}

export async function setDocumentPublished(id: string, published: boolean): Promise<void> {
  const r = await fetch(`/api/documents/${encodeURIComponent(id)}/publish`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    credentials: "same-origin",
    body: JSON.stringify({ published }),
  });
  if (!r.ok) throw new Error(`Publish failed (HTTP ${r.status})`);
}

export function downloadDocument(id: string, fileName: string): Promise<void> {
  return downloadStaffFile(`/api/documents/${encodeURIComponent(id)}/file`, fileName, "file");
}
